import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import { getByType } from '../../api/staff';
import { getContracts } from '../../api/contracts';
import { getServiceSchedules, getServiceAlerts } from '../../api/serviceSchedules';

const serviceConfig = {
  '/security': { slug: 'security', staffType: 'security', name: 'Security', icon: '🛡️', type: 'contract' },
  '/cleaning': { slug: 'cleaning', staffType: 'cleaning', name: 'Cleaning', icon: '🧹', type: 'placeholder' },
  '/water-treatment': { slug: 'water', staffType: null, name: 'Water Treatment', icon: '💧', type: 'water' },
  '/garden': { slug: 'garden', staffType: null, name: 'Garden Management', icon: '🌿', type: 'placeholder' },
  '/pool': { slug: 'pool', staffType: null, name: 'Swimming Pool', icon: '🏊', type: 'placeholder' },
};

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// WTP Equipment Data (from Aquarius Water Management O&M Manual - 12,000 LPH)
const wtpEquipment = [
  { name: 'Pressure Sand Filter', model: 'ASF 900', make: 'Pentair 3672', capacity: '12,000 LPH', size: '900mm Dia x 1800mm Ht', qty: 1, media: 'Quartz Sand (300 Kg) + Fine Sand 16/32 (650 Kg)' },
  { name: 'Iron Removal Filter', model: 'AIF 900', make: 'Pentair 3672', capacity: '12,000 LPH', size: '900mm Dia x 1800mm Ht', qty: 1, media: 'Quartz Sand (300 Kg) + DMI-65 (200 Kg) + MnO2 (250 Kg)' },
  { name: 'Activated Carbon Filter', model: 'ACF 900', make: 'Pentair 3672', capacity: '12,000 LPH', size: '900mm Dia x 1800mm Ht', qty: 1, media: 'Quartz Sand (300 Kg) + Activated Carbon Iodine 1050 (250 Kg)' },
  { name: 'Water Softener Filter', model: 'AIF 900', make: 'Pentair 3672', capacity: '12,000 LPH', size: '900mm Dia x 1800mm Ht', qty: 1, media: 'Quartz Sand (300 Kg) + Softener Resin (600 Kg)' },
  { name: 'Chemical Dosing Pump (Chlorine, Alum & pH)', model: '-', make: 'E-dose / Chemi Dose', capacity: '5 LPH @ 4 kg/cm2', size: '-', qty: 3, media: '-' },
  { name: 'Filter Feed Pump', model: 'Kds / Eqlnt', make: 'Kirloskar / Lubi', capacity: '2.0 HP, 1 Phase', size: 'Suction 40mm, Delivery 40mm', qty: 2, media: '-' },
  { name: 'Jumbo Cartridge Filter', model: '20" Jumbo Wound', make: '-', capacity: '5,000 LPH (Max)', size: '1.5" Connection', qty: 4, media: '10 Micron x 2 + 5 Micron x 2' },
  { name: 'UV Sterilizer', model: 'EFS60320', make: 'UV Plasma', capacity: '10-15 m3/hr', size: 'SS 304 Reactor', qty: 1, media: '320W Lamp, 220-240V/50-60Hz' },
];

const wtpDetails = [
  { label: 'Plant Capacity', value: '12,000 LPH', icon: '📊' },
  { label: 'Contractor', value: 'Aquarius Water Management Pvt Ltd', icon: '🏢' },
  { label: 'Max Operating Pressure', value: '4.0 Bar', icon: '⚙️' },
  { label: 'Min Infeed Pressure', value: '2.0 Bar', icon: '💧' },
  { label: 'Softener Regeneration', value: '~Twice/week (90 Kg Salt, ~3,000 INR/fill)', icon: '♻️' },
  { label: 'Backwash Frequency', value: 'Every 2 Days', icon: '🔄' },
  { label: 'Contact', value: 'Mr. Mohammed Asharaf - 98950 46362', icon: '📞' },
];

const maintenanceSchedule = [
  { task: 'Filter Backwash (Sand, Iron, Carbon)', frequency: 'Every 2 Days', description: 'Backwash to flush accumulated waste, rinse till clear, then switch to filtration mode', status: 'upcoming' },
  { task: 'Extended Backwash (All Filters)', frequency: 'Weekly', description: 'Minimum 15 minutes backwash to keep bed clean', status: 'upcoming' },
  { task: 'Jumbo Cartridge Filter - Inspect & Clean', frequency: 'Monthly', description: 'Open housing, clean 10 & 5 micron cartridges', status: 'upcoming' },
  { task: 'Chemical Dosing Levels Check', frequency: 'Daily', description: 'Check chlorine, alum & soda ash levels in dosing tanks', status: 'upcoming' },
  { task: 'Valve Inspection & Lubrication', frequency: 'Every 6 Months', description: 'Check seating, change gland packing/diaphragm, lubricate spindle', status: 'on_track' },
  { task: 'Jumbo Cartridge Filter - Replace', frequency: 'Every 6 Months', description: 'Replace all 4 cartridge filters with new ones', status: 'on_track' },
  { task: 'Vessel Internal Inspection', frequency: 'Yearly', description: 'Remove media, check vessel condition, replace filter media if required', status: 'on_track' },
  { task: 'Filter Media Replacement', frequency: 'Yearly', description: 'Replace sand, carbon, DMI-65, MnO2 and resin media as needed', status: 'on_track' },
  { task: 'Softener Salt Regeneration', frequency: '~Twice per Week', description: 'Currently done every ~300,000L. 90 Kg salt in 300L water. Cost ~3,000 INR per fill. (Ideal: every 164,200L)', status: 'upcoming' },
];

const troubleshooting = [
  { problem: 'Turbidity Breakthrough', cause: 'Change in raw water quality', action: 'Analyze raw water & backwash filters' },
  { problem: 'Loss of Media', cause: 'Broken internals / high backwash flow', action: 'Change laterals or rectify internals / control backwash flow' },
  { problem: 'High Pressure Drop', cause: 'Filter media dirty', action: 'Give backwash immediately' },
  { problem: 'Mud Ball Formation', cause: 'Change in raw water', action: 'Extended backwash / replace filter media if persistent' },
];

export default function ServiceDetailPage() {
  const location = useLocation();
  const config = serviceConfig[location.pathname] || serviceConfig['/security'];

  const [activeTab, setActiveTab] = useState('overview');
  const [staff, setStaff] = useState([]);
  const [contract, setContract] = useState(null);
  const [serviceSchedules, setServiceSchedules] = useState([]);
  const [serviceAlerts, setServiceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const tabs = config.type === 'water'
    ? [{ key: 'overview', label: 'Overview' }, { key: 'equipment', label: 'Equipment' }, { key: 'maintenance', label: 'Maintenance' }, { key: 'troubleshooting', label: 'Troubleshooting' }]
    : config.type === 'contract'
      ? [{ key: 'overview', label: 'Overview' }, { key: 'contract', label: 'Current Contract' }, { key: 'staff', label: 'Staff Details' }, { key: 'history', label: 'History' }]
      : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch contract for this service
      const contractRes = await getContracts();
      const allContracts = contractRes.data?.data || contractRes.data || [];
      const serviceContract = allContracts.find((c) =>
        c.service_category?.slug === config.slug && c.status === 'active'
      );
      setContract(serviceContract || null);

      // Fetch staff if applicable
      if (config.staffType) {
        const staffRes = await getByType(config.staffType);
        const data = staffRes.data?.data || staffRes.data || [];
        setStaff(Array.isArray(data) ? data : []);
      }

      // Fetch service schedules for water treatment
      if (config.type === 'water') {
        const [schedulesRes, alertsRes] = await Promise.all([
          getServiceSchedules(),
          getServiceAlerts(),
        ]);
        setServiceSchedules(schedulesRes.data || []);
        setServiceAlerts(alertsRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load service data', err);
    } finally {
      setLoading(false);
    }
  }, [config.slug, config.staffType, config.type]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <AppLayout title={config.name} breadcrumbs={`Services > ${config.name}`}>
        <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>
          Loading...
        </div>
      </AppLayout>
    );
  }

  // Placeholder page for services without detailed views
  if (config.type === 'placeholder') {
    return (
      <AppLayout title={config.name} breadcrumbs={`Services > ${config.name}`}>
        <div style={{ padding: '32px 0' }}>
          <div className="bg-white text-center" style={{ borderRadius: '16px', border: '1px solid #E5E5E0', padding: '80px 40px' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>{config.icon}</div>
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1A1A1A', marginBottom: '8px' }}>
              {config.name} Service
            </h3>
            <p style={{ fontSize: '14px', color: '#666666', marginBottom: '32px', maxWidth: '400px', margin: '0 auto 32px' }}>
              This service has not been configured yet. Set up the service details, assign staff, and manage contracts.
            </p>
            <button
              className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
              style={{ padding: '12px 24px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
            >
              Configure Service
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Security (contract-based) service detail
  if (config.type === 'contract') {
    return (
      <AppLayout title={config.name} breadcrumbs={`Services > ${config.name}`}>
        {/* Sub Navigation */}
        <div
          className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
          style={{ background: 'white', borderBottom: '1px solid #E5E5E0', padding: '0 40px', gap: '32px' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
              style={{
                padding: '16px 4px', fontSize: '14px', fontWeight: 500, fontFamily: "'Lexend', sans-serif",
                color: activeTab === tab.key ? '#043959' : '#666666',
                borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
              }}
              onMouseEnter={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.color = '#043959'; e.currentTarget.style.borderBottomColor = '#F4A460'; } }}
              onMouseLeave={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.color = '#666666'; e.currentTarget.style.borderBottomColor = 'transparent'; } }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '32px 0' }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>
                {config.name} Service Overview
              </h3>

              {contract ? (
                <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Current Provider</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px' }}>
                    {[
                      { label: 'Vendor', value: contract.vendor?.name || '-' },
                      { label: 'Contact Person', value: contract.vendor?.contact_person || '-' },
                      { label: 'Phone', value: contract.vendor?.phone || '-' },
                      { label: 'Email', value: contract.vendor?.email || '-' },
                      { label: 'Contract Value', value: `${formatCurrency(contract.contract_value)} / ${contract.value_frequency}` },
                      { label: 'Contract Period', value: `${formatDate(contract.start_date)} - ${formatDate(contract.end_date)}` },
                      { label: 'Status', value: contract.status },
                      { label: 'Description', value: contract.description || '-' },
                    ].map((item) => (
                      <div key={item.label} style={{ padding: '12px', background: '#FAFAF8', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white text-center" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '40px', marginBottom: '24px' }}>
                  <p style={{ fontSize: '14px', color: '#999999' }}>No active contract found for this service.</p>
                </div>
              )}

              {/* Quick stats */}
              <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '16px' }}>
                <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{staff.length}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Total Staff</div>
                </div>
                <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{staff.filter((s) => s.is_active).length}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Active</div>
                </div>
                <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>{contract ? formatCurrency(contract.contract_value) : '-'}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Monthly Cost</div>
                </div>
                <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: contract ? '#10B981' : '#999999' }}>{contract ? 'Active' : 'None'}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Contract Status</div>
                </div>
              </div>
            </>
          )}

          {/* Contract Tab */}
          {activeTab === 'contract' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Current Contract Details</h3>
              {contract ? (
                <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', overflow: 'hidden' }}>
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        { label: 'Vendor Name', value: contract.vendor?.name },
                        { label: 'Contact Person', value: contract.vendor?.contact_person },
                        { label: 'Phone', value: contract.vendor?.phone },
                        { label: 'Email', value: contract.vendor?.email },
                        { label: 'Contract Value', value: `${formatCurrency(contract.contract_value)} / ${contract.value_frequency}` },
                        { label: 'Payment Frequency', value: contract.payment_frequency },
                        { label: 'Start Date', value: formatDate(contract.start_date) },
                        { label: 'End Date', value: formatDate(contract.end_date) },
                        { label: 'Status', value: contract.status },
                        { label: 'Description', value: contract.description },
                      ].map((row, i) => (
                        <tr key={row.label} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '14px 20px', fontSize: '13px', fontWeight: 600, color: '#666666', width: '200px', background: i % 2 === 0 ? '#FAFAF8' : 'white' }}>
                            {row.label}
                          </td>
                          <td style={{ padding: '14px 20px', fontSize: '14px', color: '#1A1A1A', background: i % 2 === 0 ? '#FAFAF8' : 'white' }}>
                            {row.value || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white text-center" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '40px' }}>
                  <p style={{ fontSize: '14px', color: '#999999' }}>No active contract found.</p>
                </div>
              )}
            </>
          )}

          {/* Staff Tab */}
          {activeTab === 'staff' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>
                {config.name} Staff ({staff.length})
              </h3>
              {staff.length === 0 ? (
                <div className="bg-white text-center" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '40px' }}>
                  <p style={{ fontSize: '14px', color: '#999999' }}>No staff members assigned to this service.</p>
                </div>
              ) : (
                <div className="bg-white overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #E5E5E0' }}>
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                          {['Name', 'Position/Area', 'Shift Time', 'Working Days', 'Contact', 'Status'].map((h) => (
                            <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {staff.map((member) => {
                          const assignments = member.assignments || [];
                          const posts = assignments.map((a) => a.security_post).filter(Boolean);
                          return (
                            <tr key={member.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                              <td style={{ padding: '14px 16px' }}>
                                <div className="flex items-center gap-3">
                                  <div
                                    className="flex items-center justify-center text-white font-semibold"
                                    style={{ width: '36px', height: '36px', borderRadius: '50%', fontSize: '12px', background: 'linear-gradient(135deg, #043959, #F4A460)', flexShrink: 0 }}
                                  >
                                    {getInitials(member.name)}
                                  </div>
                                  <strong style={{ fontSize: '14px', color: '#1A1A1A' }}>{member.name}</strong>
                                </div>
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A' }}>
                                {posts.length > 0 ? posts.join(', ') : '-'}
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A' }}>
                                {formatTime12(member.work_start_time)} - {formatTime12(member.work_end_time)}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <div className="flex flex-wrap gap-1">
                                  {(member.working_days || []).map((day) => (
                                    <span
                                      key={day}
                                      style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: '#043959', color: 'white' }}
                                    >
                                      {day.slice(0, 3)}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A' }}>
                                {member.phone || '-'}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <span
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                                    background: member.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                    color: member.is_active ? '#10B981' : '#F59E0B',
                                  }}
                                >
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                                  {member.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* History Tab */}
          {activeTab === 'history' && config.type === 'contract' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Service History</h3>
              <div className="bg-white text-center" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '40px' }}>
                <p style={{ fontSize: '14px', color: '#999999' }}>Service history will appear here once events are recorded.</p>
              </div>
            </>
          )}
        </div>
      </AppLayout>
    );
  }

  // Water Treatment — rich dashboard
  if (config.type === 'water') {
    return (
      <AppLayout title={config.name} breadcrumbs={`Services > ${config.name}`}>
        {/* Sub Navigation */}
        <div
          className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
          style={{ background: 'white', borderBottom: '1px solid #E5E5E0', padding: '0 40px', gap: '32px' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
              style={{
                padding: '16px 4px', fontSize: '14px', fontWeight: 500, fontFamily: "'Lexend', sans-serif",
                color: activeTab === tab.key ? '#043959' : '#666666',
                borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
              }}
              onMouseEnter={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.color = '#043959'; e.currentTarget.style.borderBottomColor = '#F4A460'; } }}
              onMouseLeave={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.color = '#666666'; e.currentTarget.style.borderBottomColor = 'transparent'; } }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '32px 0' }}>
          {/* Service Alerts Banner */}
          {serviceAlerts.length > 0 && (
            <div
              className="flex items-start gap-4 p-5 rounded-xl relative border-l-4 mb-6"
              style={{
                borderLeftColor: serviceAlerts.some((a) => a.days_until_due <= 7) ? '#EF4444' : serviceAlerts.some((a) => a.days_until_due <= 15) ? '#F97316' : '#EAB308',
                background: serviceAlerts.some((a) => a.days_until_due <= 7)
                  ? 'linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.02) 100%)'
                  : serviceAlerts.some((a) => a.days_until_due <= 15)
                    ? 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(249,115,22,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(234,179,8,0.08) 0%, rgba(234,179,8,0.02) 100%)',
                boxShadow: '0 4px 6px rgba(0,0,0,0.06)',
              }}
            >
              <span className="text-[32px] leading-none flex-shrink-0">🔧</span>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1.5">
                  {serviceAlerts.length} Service {serviceAlerts.length === 1 ? 'Item' : 'Items'} Due Soon
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {serviceAlerts.map((alert) => (
                    <p key={alert.id} className="text-sm leading-relaxed" style={{ margin: 0, color: alert.days_until_due <= 7 ? '#DC2626' : alert.days_until_due <= 15 ? '#EA580C' : '#CA8A04' }}>
                      <strong>{alert.service_name}</strong> — {alert.is_overdue ? 'Overdue!' : `${alert.days_until_due} days remaining`}
                      {alert.estimated_cost && ` (Est. ${formatCurrency(alert.estimated_cost)})`}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Status Banner */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #043959, #065a8c)',
                  borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', color: 'white',
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600 }}>Water Treatment Plant - 12,000 LPH</span>
                    </div>
                    <p style={{ fontSize: '13px', opacity: 0.9 }}>Aquarius Water Management Pvt Ltd | Project: Garden Court, Trivandrum</p>
                  </div>
                </div>
              </div>

              {/* WTP Details Cards */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Plant Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: '16px' }}>
                  {wtpDetails.map((item) => (
                    <div key={item.label} className="flex items-start gap-3" style={{ padding: '12px', background: '#FAFAF8', borderRadius: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '2px' }}>{item.label}</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Provider Info */}
              {contract && (
                <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Contract Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px' }}>
                    {[
                      { label: 'Provider', value: contract.vendor?.name },
                      { label: 'Contact Person', value: contract.vendor?.contact_person },
                      { label: 'Phone', value: contract.vendor?.phone },
                      { label: 'Email', value: contract.vendor?.email },
                      { label: 'Contract Value', value: `${formatCurrency(contract.contract_value)} / ${contract.value_frequency}` },
                      { label: 'Contract Period', value: `${formatDate(contract.start_date)} - ${formatDate(contract.end_date)}` },
                    ].map((item) => (
                      <div key={item.label} style={{ padding: '12px', background: '#FAFAF8', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{item.value || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Process Flow */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Treatment Process Flow</h4>
                <div className="flex flex-wrap items-center gap-2" style={{ justifyContent: 'center' }}>
                  {[
                    'Raw Water Tank', 'Chlorine Dosing', 'Alum Dosing', 'pH Boosting',
                    'Sand Filter', 'Iron Removal Filter', 'Activated Carbon Filter',
                    'Water Softener', 'Cartridge Filter (10 & 5 Micron)', 'UV Sterilizer', 'Treated Water Tank',
                  ].map((step, i, arr) => (
                    <React.Fragment key={step}>
                      <div
                        style={{
                          padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                          background: i === 0 || i === arr.length - 1 ? '#043959' : '#FAFAF8',
                          color: i === 0 || i === arr.length - 1 ? 'white' : '#1A1A1A',
                          border: i === 0 || i === arr.length - 1 ? 'none' : '1px solid #E5E5E0',
                        }}
                      >
                        {step}
                      </div>
                      {i < arr.length - 1 && (
                        <span style={{ fontSize: '16px', color: '#999999' }}>→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Operational Notes */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginTop: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Operational Notes</h4>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {[
                    { title: 'Filter Sequence', text: 'Sand Filter → Iron Removal Filter → Carbon Filter → Softener Filter. The first three filters follow the same backwash → clarify → media placement → filtration cycle.' },
                    { title: 'Backwashing', text: 'Required once every 2 days. Waste and impurities accumulate during normal operation. Regular backwashing flushes out stored waste and maintains filter efficiency.' },
                    { title: 'Softener Regeneration', text: 'Salt-based regeneration using 90 Kg salt in 300L water. Currently replaced after ~300,000 litres (ideal: 164,000L). Cost ~3,000 INR per fill, done approximately twice a week.' },
                    { title: 'Monitoring', text: 'Regular monitoring of filter performance, chemical dosing, and backwashing cycles is essential. Any abnormal color, smell, or pressure drop should be reported immediately to maintenance staff or the association.' },
                  ].map((note) => (
                    <div key={note.title} style={{ padding: '14px 16px', background: '#FAFAF8', borderRadius: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#043959', marginBottom: '4px' }}>{note.title}</div>
                      <div style={{ fontSize: '13px', color: '#666666', lineHeight: '1.5' }}>{note.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Equipment & Technical Data</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
                {wtpEquipment.map((eq) => (
                  <div key={eq.name} className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '20px' }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{eq.name}</h4>
                      <span style={{
                        padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                        background: 'rgba(4,57,89,0.1)', color: '#043959',
                      }}>
                        Qty: {eq.qty}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {[
                        { label: 'Model', value: eq.model },
                        { label: 'Make', value: eq.make },
                        { label: 'Capacity', value: eq.capacity },
                        { label: 'Size', value: eq.size },
                      ].map((field) => (
                        <div key={field.label} style={{ padding: '8px', background: '#FAFAF8', borderRadius: '6px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 500, color: '#666666' }}>{field.label}</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{field.value}</div>
                        </div>
                      ))}
                    </div>
                    {eq.media !== '-' && (
                      <div style={{ marginTop: '8px', padding: '8px', background: '#FAFAF8', borderRadius: '6px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 500, color: '#666666' }}>Filter Media</div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{eq.media}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Maintenance Tab */}
          {activeTab === 'maintenance' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Maintenance Schedule</h3>
              <div className="bg-white overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Task', 'Frequency', 'Description', 'Status'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {maintenanceSchedule.map((item) => (
                        <tr key={item.task} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{item.task}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              display: 'inline-block', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                              background: 'rgba(4,57,89,0.08)', color: '#043959',
                            }}>
                              {item.frequency}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666666' }}>{item.description}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                              background: item.status === 'upcoming' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)',
                              color: item.status === 'upcoming' ? '#3B82F6' : '#10B981',
                            }}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                              {item.status === 'upcoming' ? 'Recurring' : 'Periodic'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Consumables & Replacement Schedule (API-driven) */}
              {serviceSchedules.length > 0 && (
                <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginTop: '24px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Consumables & Replacement Schedule</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                          {['Item', 'Interval', 'Est. Cost', 'Last Serviced', 'Next Due', 'Status'].map((h) => (
                            <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {serviceSchedules.map((item) => {
                          const daysLeft = item.days_until_due;
                          const statusColor = item.is_overdue ? '#EF4444' : daysLeft <= 7 ? '#EF4444' : daysLeft <= 15 ? '#F97316' : daysLeft <= 30 ? '#EAB308' : '#10B981';
                          const statusLabel = item.is_overdue ? 'Overdue' : daysLeft <= 30 ? `${daysLeft}d left` : 'On Track';
                          return (
                            <tr key={item.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                              <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>{item.service_name}</td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, background: 'rgba(4,57,89,0.08)', color: '#043959' }}>
                                  {item.replacement_interval_months} months
                                </span>
                              </td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A' }}>{formatCurrency(item.estimated_cost)}</td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666666' }}>{formatDate(item.last_serviced_at)}</td>
                              <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666666' }}>{formatDate(item.next_due_date)}</td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                                  background: `${statusColor}18`, color: statusColor,
                                }}>
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                                  {statusLabel}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Backwash Procedure */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginTop: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Filter Backwash Procedure</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {[
                    'Check all equipment is ready for operation',
                    'Lubricate all mechanical equipment',
                    'Ensure enough water in Raw Water Tank and Treated Water Tank',
                    'Check chemical levels in dosing tanks',
                    'Set multiport valve to "Backwash" position',
                    'Start filter infeed pump - run for 3 minutes or till water is clear',
                    'Switch off pump, set multiport valve to "Rinse" - rinse for 2 minutes',
                    'Switch off pump, set multiport valve to "Filter" position',
                    'Unit is ready for service',
                    'For best results, use treated/fresh water for backwashing',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3" style={{ padding: '10px 12px', background: i % 2 === 0 ? '#FAFAF8' : 'white', borderRadius: '8px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '24px', height: '24px', borderRadius: '50%', fontSize: '12px', fontWeight: 600,
                        background: '#043959', color: 'white', flexShrink: 0,
                      }}>
                        {i + 1}
                      </span>
                      <span style={{ fontSize: '13px', color: '#1A1A1A', paddingTop: '2px' }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Troubleshooting Tab */}
          {activeTab === 'troubleshooting' && (
            <>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Troubleshooting Guide</h3>
              <div className="bg-white overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Problem', 'Cause', 'Action'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {troubleshooting.map((item) => (
                        <tr key={item.problem} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 500, color: '#EF4444' }}>{item.problem}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666666' }}>{item.cause}</td>
                          <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{item.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Chemical Dosing Info */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '24px', marginTop: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '16px' }}>Chemical Dosing Notes</h4>
                <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '16px' }}>
                  {[
                    { title: 'Chlorine Dosing', desc: 'Liquid chlorine for oxidation of iron and disinfection. Adjust via stroke or concentration.' },
                    { title: 'Alum Dosing', desc: 'Aluminium Sulphate as flocculent - suspended particles clump and settle for easy filtration.' },
                    { title: 'pH Boosting (Soda Ash)', desc: 'Sodium Carbonate dosed to boost pH level of treated water to acceptable range.' },
                  ].map((item) => (
                    <div key={item.title} style={{ padding: '16px', background: '#FAFAF8', borderRadius: '8px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#043959', marginBottom: '6px' }}>{item.title}</div>
                      <div style={{ fontSize: '13px', color: '#666666' }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '16px', padding: '12px 16px', background: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
                    <strong>Important:</strong> Different chemicals must not be mixed in dosing tanks. Periodic checks of injection points should be carried out to ensure free flow of solutions.
                  </p>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white" style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '20px', marginTop: '24px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '8px' }}>Emergency Contact</h4>
                <p style={{ fontSize: '14px', color: '#666666', margin: 0 }}>
                  For any operational difficulties, contact: <strong style={{ color: '#1A1A1A' }}>Mr. Mohammed Asharaf</strong> - 98950 46362 / 9895026362
                </p>
              </div>
            </>
          )}
        </div>
      </AppLayout>
    );
  }

  return null;
}
